import React from "react";
import { Link } from "react-router";
import {
  partnerPortfolioService,
  PORTFOLIO_TEXTFELDER,
  type PortfolioEintrag,
} from "../services/partnerPortfolioService";
import {
  DataTable,
  type Column,
  type SortState,
} from "../components/redesign/table/DataTable";
import "../components/redesign/contactRedesign.css";
import "../components/produktpartner/partnerPortfolio.css";
const abrechnung = (row: PortfolioEintrag) =>
  row.abrechnungContactId === row.produktpartnerContactId
    ? "Direktanbindung"
    : row.abrechnungName;
const wert = (row: PortfolioEintrag, key: string) =>
  key === "abrechnungName"
    ? abrechnung(row)
    : String(row[key as keyof PortfolioEintrag] ?? "");
const columns: Column<PortfolioEintrag>[] = [
  {
    id: "produktpartnerName",
    header: "Produktpartner",
    sortable: true,
    cell: (row) => (
      <Link to={`/kontakte/edit/${row.produktpartnerContactId}`}>
        {row.produktpartnerName}
      </Link>
    ),
  },
  {
    id: "abrechnungName",
    header: "Kooperationspartner / Direktanbindung",
    sortable: true,
    cell: (row) =>
      row.abrechnungContactId === row.produktpartnerContactId ? (
        "Direktanbindung"
      ) : (
        <Link to={`/kontakte/edit/${row.abrechnungContactId}`}>
          {row.abrechnungName}
        </Link>
      ),
  },
  {
    id: "insuranceProductName",
    header: "Vertragsart gemäß KMVS",
    sortable: true,
    cell: (row) => row.insuranceProductName,
  },
  ...PORTFOLIO_TEXTFELDER.map(([key, label]) => ({
    id: key,
    header: label,
    sortable: true,
    cell: (row: PortfolioEintrag) => row[key] || "—",
  })),
];
export default function PartnerPortfolioUebersicht() {
  const [rows, setRows] = React.useState<PortfolioEintrag[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [versuch, setVersuch] = React.useState(0);
  const [suche, setSuche] = React.useState("");
  const [sort, setSort] = React.useState<SortState | null>(null);
  React.useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError("");
    void partnerPortfolioService
      .overview(abort.signal)
      .then((data) => {
        if (!abort.signal.aborted) setRows(data);
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setError(
            e instanceof Error
              ? e.message
              : "Portfolio konnte nicht geladen werden",
          );
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [versuch]);
  const sichtbar = React.useMemo(() => {
    const needle = suche.trim().toLocaleLowerCase("de");
    const result = rows.filter(
      (row) =>
        !needle ||
        columns.some((c) =>
          wert(row, c.id).toLocaleLowerCase("de").includes(needle),
        ),
    );
    if (sort)
      result.sort(
        (a, b) =>
          wert(a, sort.field).localeCompare(wert(b, sort.field), "de", {
            numeric: true,
          }) * (sort.direction === "asc" ? 1 : -1) || a.id - b.id,
      );
    return result;
  }, [rows, suche, sort]);
  return (
    <div className="kvp-rd">
      <main className="portfolio-uebersicht">
        <h1>Partnerportfolio – Gesamtübersicht</h1>
        <p>Vereinbarte Produkte und Provisionen aller Partnergesellschaften.</p>
        <label>
          Portfolio durchsuchen
          <input
            type="search"
            className="inp"
            aria-label="Portfolio durchsuchen"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Gesellschaft, Vertragsart, Produkt oder Provision"
          />
        </label>
        {loading ? (
          <p role="status">Portfolio wird geladen …</p>
        ) : error ? (
          <>
            <p role="alert" className="portfolio-fehler">
              {error}
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => setVersuch((n) => n + 1)}
            >
              Erneut laden
            </button>
          </>
        ) : (
          <>
            <p>{sichtbar.length} Einträge</p>
            <DataTable
              columns={columns}
              rows={sichtbar}
              rowKey={(r) => r.id}
              sort={sort}
              onSortChange={setSort}
              emptyText={
                suche
                  ? "Keine passenden Produkte."
                  : "Noch keine Portfolioeinträge."
              }
              cardStyle={{ overflowX: "auto" }}
            />
          </>
        )}
      </main>
    </div>
  );
}
